import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import MapView from './components/MapView';
import GridView from './components/GridView';
import LayoutToggle from './components/LayoutToggle';
import FilterDropdown from './components/FilterDropdown';
import TextFilter from './components/TextFilter';
import CompanyFilter from './components/CompanyFilter';
import CitySelector from './components/CitySelector';
import SavedSearchesButton from './components/SavedSearchesButton';
import QuickSaveButton from './components/QuickSaveButton';
import ExportButton from './components/ExportButton';
import HelpButton from './components/HelpButton';
import SavedSearchesModal from './components/SavedSearchesModal';
import ExportDataModal from './components/ExportDataModal';
import ProUpgradeModal from './components/ProUpgradeModal';
import OverviewVideoModal from './components/OverviewVideoModal';
import MobileFiltersSheet from './components/MobileFiltersSheet';
import useProjects from './hooks/useProjects';
import { useIsMobile } from './hooks/useMediaQuery';
import { useSavedSearches } from './hooks/useSavedSearches';
import { useExport } from './hooks/useExport';
import { getAvailableLayers, getInitialLayerState } from './config/layers';

const VIEW_MODES = {
  SPLIT: 'split',
  MAP: 'map',
  GRID: 'grid',
};

/**
 * Escape a plain string for safe insertion as ribbon message HTML.
 */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Pure filter: apply a saved/current filter shape to a project list.
 * Same semantics as the `filteredByFilters` useMemo below, but reusable
 * (e.g. when the user exports directly from a saved search row).
 */
function applyFilters(projects, filters) {
  if (!filters) return projects;
  const uses = filters.uses || [];
  const status = filters.status || [];
  const neighborhood = filters.neighborhood || [];
  const company = filters.company || [];
  const title = (filters.title || '').toLowerCase();

  return projects.filter((project) => {
    if (uses.length > 0) {
      const projectUses = Array.isArray(project.uses) ? project.uses : [];
      if (!projectUses.some((u) => uses.includes(u))) return false;
    }
    if (status.length > 0 && !status.includes(project.status_label)) return false;
    if (neighborhood.length > 0 && !neighborhood.includes(project.neighborhood)) return false;
    if (company.length > 0) {
      const projectCompanies = Array.isArray(project.companies) ? project.companies : [];
      if (!projectCompanies.some((c) => company.includes(c))) return false;
    }
    if (title) {
      const projectTitle = (project.title || '').toLowerCase();
      if (!projectTitle.includes(title)) return false;
    }
    return true;
  });
}

function App({ apiUrl, mapboxToken, mapboxStyle, cityName = 'Urbanize' }) {
  const [viewMode, setViewMode] = useState(VIEW_MODES.MAP);
  const [mapBounds, setMapBounds] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    uses: [],
    status: [],
    neighborhood: [],
    company: [],
    title: '',
  });

  // Track if user has changed filters since entering grid view
  const [gridFiltersChanged, setGridFiltersChanged] = useState(false);
  const frozenBoundsRef = useRef(null);
  const lastBoundsRef = useRef(null);
  const mapRef = useRef(null);

  // User authentication and subscription state
  const [userInfo, setUserInfo] = useState({ isAuthenticated: false, isSubscriber: false });
  const [userLoading, setUserLoading] = useState(true);

  // Saved searches modal state
  const [showSavedSearchesModal, setShowSavedSearchesModal] = useState(false);
  const [quickSaveLoading, setQuickSaveLoading] = useState(false);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportContext, setExportContext] = useState({
    filters: null,
    projectNids: [],
    searchName: null,
    savedSearchId: null,
  });

  // Pro upgrade modal state
  const [showProUpgradeModal, setShowProUpgradeModal] = useState(false);
  const [proUpgradeFeature, setProUpgradeFeature] = useState('save unlimited searches');

  // Overview video modal state
  const [showOverviewModal, setShowOverviewModal] = useState(false);

  // Phone layout: filters collapse into a bottom sheet (see #311).
  const isMobile = useIsMobile();
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);
  // Synchronous mirror of `showFiltersSheet`, so a filter change fired in
  // the same tap that closes the sheet (flushing a pending title search)
  // already frames the map as if the sheet were gone.
  const filtersSheetOpenRef = useRef(false);
  const filtersSheetRef = useRef(null);
  const mapPanelRef = useRef(null);
  const openFiltersSheet = useCallback(() => {
    filtersSheetOpenRef.current = true;
    setShowFiltersSheet(true);
  }, []);
  const closeFiltersSheet = useCallback(() => {
    filtersSheetOpenRef.current = false;
    setShowFiltersSheet(false);
  }, []);
  useEffect(() => {
    if (!isMobile) closeFiltersSheet();
  }, [isMobile, closeFiltersSheet]);

  const { projects, center, domain, cities, neighborhoods, companies, loading, error } = useProjects(apiUrl);

  // Saved searches hook
  const { 
    searches: savedSearches, 
    loading: savedSearchesLoading,
    saveSearch,
    updateSearch,
    deleteSearch,
    markViewed,
  } = useSavedSearches(userInfo.isSubscriber);

  // Export hook (quota + data export)
  const {
    quota: exportQuota,
    quotaLoading: exportQuotaLoading,
    exportData,
  } = useExport(userInfo.isSubscriber);

  // Check user authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Drupal staff with the paywall bypass permission are treated as Pro subscribers.
      if (window.drupalSettings?.urbanize?.paywall?.bypass) {
        setUserInfo({ isAuthenticated: true, isSubscriber: true });
        setUserLoading(false);
        return;
      }

      if (typeof window.urbanizeMembership === 'undefined' || 
          typeof window.urbanizeMembership.getCurrentUserInfo !== 'function') {
        setUserInfo({ isAuthenticated: false, isSubscriber: false });
        setUserLoading(false);
        return;
      }

      try {
        const info = await window.urbanizeMembership.getCurrentUserInfo();
        setUserInfo({
          isAuthenticated: info.isAuthenticated || false,
          isSubscriber: info.isSubscriber || false,
        });
      } catch (err) {
        console.warn('Error checking user auth:', err);
        setUserInfo({ isAuthenticated: false, isSubscriber: false });
      } finally {
        setUserLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Show a notification via the site-wide status ribbon (Drupal.Message), so the
  // map shares one consistent notification system with the rest of the site.
  // Plain-string messages are escaped and rendered as text; pass { html } to opt
  // into trusted markup. Returns the message id (for later removal), or null.
  const showNotification = useCallback((message, type = 'success', options = {}) => {
    const typeMap = { success: 'status', error: 'error', info: 'status', warning: 'warning' };
    const drupalType = typeMap[type] || 'status';
    const html = typeof message === 'string'
      ? escapeHtml(message)
      : (message && message.html) || '';

    if (!window.Drupal || !window.Drupal.Message) {
      (type === 'error' ? console.error : console.log)(html);
      return null;
    }

    try {
      return new window.Drupal.Message().add(html, { type: drupalType, persist: !!options.persist });
    } catch (err) {
      console.error('Failed to show notification:', err);
      return null;
    }
  }, []);

  // Remove a specific ribbon message by id.
  const hideNotification = useCallback((id) => {
    if (!id || !window.Drupal || !window.Drupal.Message) return;
    try {
      new window.Drupal.Message().remove(id);
    } catch (err) {
      // No-op: message may already be gone.
    }
  }, []);

  // The ribbon renders outside React, so wire the inline "click here" link in the
  // quick-save message via the DOM once it has been added.
  const wireOpenSavedSearchesLink = useCallback((id) => {
    const scope = id ? document.querySelector(`[data-drupal-message-id="${id}"]`) : document;
    const link = scope && scope.querySelector('[data-map-open-saved-searches]');
    if (!link) return;
    link.addEventListener('click', (e) => {
      e.preventDefault();
      hideNotification(id);
      setShowSavedSearchesModal(true);
    }, { once: true });
  }, [hideNotification]);

  // Generate default name for quick save
  const generateDefaultName = useCallback(() => {
    const citySearches = savedSearches.filter(s => 
      s.name.toLowerCase().startsWith(`${cityName.toLowerCase()} search `)
    );
    const numbers = citySearches.map(s => {
      const match = s.name.match(/search (\d+)$/i);
      return match ? parseInt(match[1], 10) : 0;
    });
    const nextNum = Math.max(0, ...numbers) + 1;
    return `${cityName} search ${nextNum}`;
  }, [cityName, savedSearches]);

  // Handle quick save button click
  const handleQuickSave = useCallback(async () => {
    if (!userInfo.isSubscriber) {
      // Show upgrade modal for non-subscribers
      setProUpgradeFeature('save unlimited searches');
      setShowProUpgradeModal(true);
      return;
    }

    setQuickSaveLoading(true);
    const name = generateDefaultName();
    const result = await saveSearch(name, activeFilters);
    setQuickSaveLoading(false);

    if (result.success) {
      const html =
        `Your search has been saved as "<strong>${escapeHtml(name)}</strong>". ` +
        'You can access, edit, and set up alerts for this search by clicking the ' +
        '<svg style="vertical-align:middle;display:inline-block;margin:0 4px" width="16" height="16" ' +
        'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
        '<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg> ' +
        'button or by <a href="#" data-map-open-saved-searches>clicking here</a>.';
      const id = showNotification({ html }, 'success', { persist: true });
      wireOpenSavedSearchesLink(id);
    } else {
      showNotification(`Failed to save search: ${result.error}`, 'error');
    }
  }, [userInfo.isSubscriber, generateDefaultName, saveSearch, activeFilters, showNotification, wireOpenSavedSearchesLink]);

  // Handle save from modal
  const handleSaveFromModal = useCallback(async (name) => {
    const result = await saveSearch(name, activeFilters);
    if (result.success) {
      showNotification(`Your search has been saved as "${name}".`, 'success');
    } else {
      showNotification(`Failed to save search: ${result.error}`, 'error');
    }
  }, [saveSearch, activeFilters, showNotification]);

  // While the mobile filters sheet is open it covers the lower part of the
  // map, so frame the matching pins in the strip of map still visible above
  // it (see #311). Returns the extra camera padding for fitToProjects, or
  // undefined when nothing covers the map (desktop, sheet closed).
  const getSheetFitOptions = useCallback(() => {
    if (!filtersSheetOpenRef.current) return undefined;
    const sheetRect = filtersSheetRef.current && filtersSheetRef.current.measure();
    const panel = mapPanelRef.current;
    if (!sheetRect || !panel) return undefined;

    // The sheet is fixed to the bottom of the viewport; derive its top edge
    // from its height (which grows as accordions expand) rather than reading
    // `top`, which is skewed while its slide-up transform is still running.
    const sheetTop = window.innerHeight - sheetRect.height;
    const covered = panel.getBoundingClientRect().bottom - sheetTop;
    if (covered <= 0) return undefined;
    return { padding: { bottom: Math.round(covered) + 16 } };
  }, []);

  // On the map/split layouts, frame the map to the projects matching the given
  // filters and reset to a flat, top-down 2D view (see #215). Used both when a
  // saved search is loaded and whenever a filter changes. We compute the
  // matching projects directly from the passed filters so the framing doesn't
  // depend on the async filter-state update. No-op on grid view (map hidden).
  const fitMapToFilters = useCallback((filters) => {
    if (viewMode !== VIEW_MODES.MAP && viewMode !== VIEW_MODES.SPLIT) return;
    if (!mapRef.current || !mapRef.current.fitToProjects) return;
    const matching = applyFilters(projects, filters);
    mapRef.current.fitToProjects(matching, getSheetFitOptions());
  }, [viewMode, projects, getSheetFitOptions]);

  // Handle load search
  const handleLoadSearch = useCallback((search) => {
    const nextFilters = search.filters
      ? {
          uses: search.filters.uses || [],
          status: search.filters.status || [],
          neighborhood: search.filters.neighborhood || [],
          company: search.filters.company || [],
          title: search.filters.title || '',
        }
      : null;

    if (nextFilters) {
      setActiveFilters(nextFilters);
    }

    fitMapToFilters(nextFilters || activeFilters);

    // Mark the search as viewed to update last_viewed_at
    markViewed(search.id);
  }, [markViewed, activeFilters, fitMapToFilters]);

  // Handle delete search
  const handleDeleteSearch = useCallback(async (id, name) => {
    const result = await deleteSearch(id);
    if (result.success) {
      showNotification(`"${name}" has been deleted.`, 'success');
    } else {
      showNotification(`Failed to delete search: ${result.error}`, 'error');
    }
  }, [deleteSearch, showNotification]);

  // Handle update search (rename)
  const handleUpdateSearch = useCallback(async (id, updates, originalName) => {
    const result = await updateSearch(id, updates);
    if (result.success && updates.name) {
      showNotification(`"${originalName}" has been renamed to "${updates.name}".`, 'success');
    } else if (!result.success) {
      showNotification(`Failed to update search: ${result.error}`, 'error');
    }
  }, [updateSearch, showNotification]);

  // Handle toggle notifications
  const handleToggleNotifications = useCallback(async (id, enabled, name) => {
    const result = await updateSearch(id, { notifications_enabled: enabled });
    if (result.success) {
      const status = enabled ? 'enabled' : 'disabled';
      showNotification(`Email notifications for "${name}" have been ${status}.`, 'success');
    } else {
      showNotification(`Failed to update notifications: ${result.error}`, 'error');
    }
  }, [updateSearch, showNotification]);

  // Handle My Searches button click
  const handleOpenSavedSearches = useCallback(() => {
    if (!userInfo.isSubscriber) {
      // Show upgrade modal for non-subscribers
      setProUpgradeFeature('save and manage unlimited searches');
      setShowProUpgradeModal(true);
      return;
    }
    setShowSavedSearchesModal(true);
  }, [userInfo.isSubscriber]);

  // Handle Export Data button in the map nav.
  // We recompute the matching nids inline (via applyFilters) rather than
  // reading the `filteredByFilters` useMemo, so this handler can sit above
  // the memo without hitting a temporal-dead-zone ReferenceError in its
  // dependency array.
  const handleOpenExportFromNav = useCallback(() => {
    if (!userInfo.isSubscriber) {
      setProUpgradeFeature('export project data');
      setShowProUpgradeModal(true);
      return;
    }
    const matching = applyFilters(projects, activeFilters);
    setExportContext({
      filters: activeFilters,
      projectNids: matching.map((p) => p.nid),
      searchName: null,
      savedSearchId: null,
    });
    setShowExportModal(true);
  }, [userInfo.isSubscriber, activeFilters, projects]);

  // Handle Export button from within a saved search row.
  // The SavedSearchesModal is already gated so no Pro check is needed here.
  const handleOpenExportFromSavedSearch = useCallback((search) => {
    const searchFilters = search.filters || {};
    const matching = applyFilters(projects, searchFilters);
    setExportContext({
      filters: searchFilters,
      projectNids: matching.map((p) => p.nid),
      searchName: search.name || null,
      savedSearchId: search.id || null,
    });
    setShowSavedSearchesModal(false);
    setShowExportModal(true);
  }, [projects]);

  // Perform the export (delegated from ExportDataModal).
  const handleExport = useCallback(async (payload) => {
    const result = await exportData(payload);
    if (result.success) {
      const label = payload.format === 'xlsx' ? 'Excel' : payload.format.toUpperCase();
      let msg = `Exported ${result.exportCount} project${result.exportCount === 1 ? '' : 's'} as ${label}.`;
      if (result.truncated) {
        msg += ` (Monthly limit reached; ${result.requestedCount - result.exportCount} skipped.)`;
      }
      showNotification(msg, 'success');
    } else if (result.error === 'quota_exceeded') {
      showNotification(result.message || 'Monthly export limit reached.', 'error');
    } else if (result.error === 'Pro subscription required') {
      showNotification('A Pro subscription is required to export data.', 'error');
    } else if (result.error) {
      showNotification(`Export failed: ${result.message || result.error}`, 'error');
    }
    return result;
  }, [exportData, showNotification]);

  // Deep-link: if URL has ?project={nid}, fly to that project once data loads.
  useEffect(() => {
    if (loading || !projects.length) return;
    const params = new URLSearchParams(window.location.search);
    const targetNid = params.get('project');
    if (!targetNid) return;

    const project = projects.find((p) => String(p.nid) === targetNid);
    if (project && mapRef.current && mapRef.current.highlightProject) {
      // Small delay to ensure map is fully loaded before flying.
      setTimeout(() => mapRef.current.highlightProject(project), 500);
    }

    // Clean the param from the URL so refreshes don't re-trigger.
    params.delete('project');
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
  }, [loading, projects]);

  // Deep-link: if URL has ?overview=1 or utm_campaign=map-paywall-relaunch,
  // auto-open the overview video modal once.
  // Mirrors the ?project deep-link above: read the param, act, then strip it via
  // history.replaceState so a refresh doesn't re-trigger. This is the parameter
  // the campaign's "Explore the Map" link points to.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasOverviewParam = params.get('overview') === '1';
    const isCampaign = params.get('utm_campaign') === 'map-paywall-relaunch';
    if (!hasOverviewParam && !isCampaign) return;

    // Campaign UTM params must stay in the URL for GA4 attribution, so
    // re-triggering on refresh is gated with a session flag instead of by
    // stripping them.
    const seenKey = 'urbanize_overview_video_campaign_seen';
    let seenThisSession = false;
    try {
      seenThisSession = !!window.sessionStorage.getItem(seenKey);
      if (isCampaign) window.sessionStorage.setItem(seenKey, '1');
    } catch (e) {
      // Storage unavailable (e.g. privacy mode): treat as not yet seen.
    }

    if (hasOverviewParam || !seenThisSession) {
      setShowOverviewModal(true);
    }

    if (!hasOverviewParam) return;

    // Clean the param from the URL so refreshes don't re-trigger.
    params.delete('overview');
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
  }, []);

  // Get available layers for this domain
  const availableLayers = useMemo(() => {
    return getAvailableLayers(domain);
  }, [domain]);

  // Layer visibility state
  const [layerVisibility, setLayerVisibility] = useState({});

  // Initialize layer visibility when domain loads
  useMemo(() => {
    if (domain) {
      setLayerVisibility(getInitialLayerState(domain));
    }
  }, [domain]);

  // Handle layer toggle
  const handleLayerToggle = useCallback((layerId, isVisible) => {
    setLayerVisibility((prev) => ({
      ...prev,
      [layerId]: isVisible,
    }));
  }, []);

  // Extract unique filter options from projects data
  const filterOptions = useMemo(() => {
    const usesSet = new Set();
    const statusSet = new Set();

    projects.forEach((project) => {
      if (Array.isArray(project.uses)) {
        project.uses.forEach((use) => use && usesSet.add(use));
      }
      if (project.status_label) {
        statusSet.add(project.status_label);
      }
    });

    return {
      uses: Array.from(usesSet).sort(),
      status: Array.from(statusSet).sort(),
      // Use API-provided neighborhoods (filtered by domain)
      neighborhood: neighborhoods,
    };
  }, [projects, neighborhoods]);

  // Filter projects by active filters (no location filtering)
  const filteredByFilters = useMemo(
    () => applyFilters(projects, activeFilters),
    [projects, activeFilters],
  );

  // Helper function to filter by bounds
  const filterByBounds = useCallback((projectList, bounds) => {
    if (!bounds) return projectList;

    return projectList.filter((project) => {
      const { lat, lon } = project;
      return (
        lat >= bounds.south &&
        lat <= bounds.north &&
        lon >= bounds.west &&
        lon <= bounds.east
      );
    });
  }, []);

  // Targeted projects - the projects shown in grid and counted
  const targetedProjects = useMemo(() => {
    if (viewMode === VIEW_MODES.MAP) {
      return filterByBounds(filteredByFilters, mapBounds);
    }

    if (viewMode === VIEW_MODES.SPLIT) {
      return filterByBounds(filteredByFilters, mapBounds);
    }

    if (viewMode === VIEW_MODES.GRID) {
      if (gridFiltersChanged) {
        return filteredByFilters;
      } else {
        return filterByBounds(filteredByFilters, frozenBoundsRef.current);
      }
    }

    return filteredByFilters;
  }, [viewMode, filteredByFilters, mapBounds, gridFiltersChanged, filterByBounds]);

  // Handle bounds change from map
  const handleBoundsChange = useCallback((bounds) => {
    setMapBounds(bounds);
    lastBoundsRef.current = bounds;
  }, []);

  // Handle view mode change
  const handleViewModeChange = useCallback((newMode) => {
    if (newMode === VIEW_MODES.GRID) {
      frozenBoundsRef.current = lastBoundsRef.current;
      setGridFiltersChanged(false);
    }
    setViewMode(newMode);
  }, []);

  // Handle filter changes
  const handleFilterChange = useCallback((filterType, values) => {
    const nextFilters = { ...activeFilters, [filterType]: values };
    setActiveFilters(nextFilters);

    if (viewMode === VIEW_MODES.GRID) {
      setGridFiltersChanged(true);
    } else {
      // Re-fit the map to the new set of matching pins (see #215).
      fitMapToFilters(nextFilters);
    }
  }, [viewMode, activeFilters, fitMapToFilters]);

  // Handle title search change (already debounced by TextFilter)
  const handleTitleChange = useCallback((value) => {
    const nextFilters = { ...activeFilters, title: value };
    setActiveFilters(nextFilters);

    if (viewMode === VIEW_MODES.GRID) {
      setGridFiltersChanged(true);
    } else {
      // Re-fit the map to the new set of matching pins (see #215).
      fitMapToFilters(nextFilters);
    }
  }, [viewMode, activeFilters, fitMapToFilters]);

  // Clear every filter at once (mobile filters sheet "Clear all").
  const handleClearAllFilters = useCallback(() => {
    const nextFilters = { uses: [], status: [], neighborhood: [], company: [], title: '' };
    setActiveFilters(nextFilters);
    if (viewMode === VIEW_MODES.GRID) {
      setGridFiltersChanged(true);
    } else {
      fitMapToFilters(nextFilters);
    }
  }, [viewMode, fitMapToFilters]);

  // Tools requested from the mobile filters sheet run from this effect, one
  // render after the request, so they see the committed filter state (the
  // sheet flushes its debounced title filter in the same tap).
  const [pendingSheetTool, setPendingSheetTool] = useState(null);
  useEffect(() => {
    if (!pendingSheetTool) return;
    setPendingSheetTool(null);
    switch (pendingSheetTool) {
      case 'export': handleOpenExportFromNav(); break;
      case 'quickSave': handleQuickSave(); break;
      case 'savedSearches': handleOpenSavedSearches(); break;
      case 'help': setShowOverviewModal(true); break;
      default: break;
    }
  }, [pendingSheetTool, handleOpenExportFromNav, handleQuickSave, handleOpenSavedSearches]);

  // Number of filter groups with a selection, shown as a badge on the mobile
  // "Filters" button.
  const activeFilterCount =
    (activeFilters.uses.length > 0 ? 1 : 0) +
    (activeFilters.status.length > 0 ? 1 : 0) +
    (activeFilters.neighborhood.length > 0 ? 1 : 0) +
    (activeFilters.company.length > 0 ? 1 : 0) +
    ((activeFilters.title || '').trim() !== '' ? 1 : 0);

  // Handle highlighting a project on the map (for split view card clicks)
  const handleHighlightProject = useCallback((project) => {
    if (mapRef.current && mapRef.current.highlightProject) {
      mapRef.current.highlightProject(project);
    }
  }, []);

  if (loading) {
    return (
      <div className="project-map-container">
        <div className="project-map-loading">
          <div className="loading-spinner"></div>
          <p>Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-map-container">
        <div className="project-map-error">
          <p>Error loading projects: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`project-map-container view-mode-${viewMode}`}>
      {isMobile ? (
        /* Phone header: City + one "Filters" button that opens the bottom
           sheet, with the view toggles on the right (see #311). */
        <header className="project-map-header project-map-header--mobile">
          <div className="project-map-header-left">
            <CitySelector cities={cities} currentDomain={domain} />
            <button
              type="button"
              className={`filter-dropdown-trigger mobile-filters-trigger ${activeFilterCount > 0 ? 'has-selection' : ''}`}
              onClick={openFiltersSheet}
              aria-haspopup="dialog"
              aria-expanded={showFiltersSheet}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="7" y1="12" x2="17" y2="12" />
                <line x1="10" y1="18" x2="14" y2="18" />
              </svg>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="filter-dropdown-badge">{activeFilterCount}</span>
              )}
            </button>
          </div>
          <div className="project-map-header-right">
            <LayoutToggle
              viewMode={viewMode}
              onChange={handleViewModeChange}
            />
          </div>
        </header>
      ) : (
      <header className="project-map-header">
        <div className="project-map-header-left">
          <CitySelector cities={cities} currentDomain={domain} />
          <FilterDropdown
            label="Land Use"
            options={filterOptions.uses}
            selected={activeFilters.uses}
            onChange={(values) => handleFilterChange('uses', values)}
          />
          <FilterDropdown
            label="Status"
            options={filterOptions.status}
            selected={activeFilters.status}
            onChange={(values) => handleFilterChange('status', values)}
          />
          {/* Neighborhood filter. Project neighborhoods are now derived with an
              associated-article fallback in the map data loaders, so this
              returns meaningful results instead of matching only the handful of
              projects with a project-level neighborhood. Known limitations are
              tracked separately: it is still single-value (a project collapses
              to its most recent article's neighborhood, so cross-neighborhood
              projects only match one) and the static cache is not invalidated
              on article edits yet. See NEIGHBORHOOD_FILTER.md. */}
          <FilterDropdown
            label="Neighborhood"
            options={filterOptions.neighborhood}
            selected={activeFilters.neighborhood}
            onChange={(values) => handleFilterChange('neighborhood', values)}
          />
          <CompanyFilter
            companies={companies}
            selected={activeFilters.company}
            onChange={(values) => handleFilterChange('company', values)}
          />
          <TextFilter
            label="Search by title"
            value={activeFilters.title}
            onChange={handleTitleChange}
            placeholder="Project Name"
          />
          <span className="project-count">
            {targetedProjects.length} project{targetedProjects.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="project-map-header-right">
          {!userLoading && (
            <div className="saved-searches-buttons">
              <ExportButton
                onClick={handleOpenExportFromNav}
                isSubscriber={userInfo.isSubscriber}
              />
              <QuickSaveButton 
                onClick={handleQuickSave}
                loading={quickSaveLoading}
                isSubscriber={userInfo.isSubscriber}
              />
              <SavedSearchesButton 
                onClick={handleOpenSavedSearches}
                count={savedSearches.length}
                isSubscriber={userInfo.isSubscriber}
              />
              <HelpButton onClick={() => setShowOverviewModal(true)} />
            </div>
          )}
          <LayoutToggle
            viewMode={viewMode}
            onChange={handleViewModeChange}
          />
        </div>
      </header>
      )}

      {/* Mobile filters bottom sheet (see #311) */}
      {isMobile && (
        <MobileFiltersSheet
          ref={filtersSheetRef}
          isOpen={showFiltersSheet}
          onClose={closeFiltersSheet}
          filterOptions={filterOptions}
          companies={companies}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onTitleChange={handleTitleChange}
          onClearAll={handleClearAllFilters}
          resultCount={targetedProjects.length}
          showTools={!userLoading}
          isSubscriber={userInfo.isSubscriber}
          onRequestTool={setPendingSheetTool}
          quickSaveLoading={quickSaveLoading}
          savedSearchCount={savedSearches.length}
        />
      )}

      {/* Persistent two-panel layout */}
      <div className="project-map-content">
        <div className={`panel-left ${viewMode}`}>
          <GridView
            projects={targetedProjects}
            fullWidth={viewMode === VIEW_MODES.GRID}
            showFooter={viewMode === VIEW_MODES.GRID}
            totalCount={targetedProjects.length}
            viewMode={viewMode}
            onHighlight={handleHighlightProject}
          />
        </div>
        <div className={`panel-right ${viewMode}`} ref={mapPanelRef}>
          <MapView
            ref={mapRef}
            projects={filteredByFilters}
            center={center}
            mapboxToken={mapboxToken}
            mapboxStyle={mapboxStyle}
            onBoundsChange={handleBoundsChange}
            availableLayers={availableLayers}
            layerVisibility={layerVisibility}
            onLayerToggle={handleLayerToggle}
          />
          {/* Phone-only project count, floating over the map so it stays
              visible (and live) while the filters sheet is open; the header
              count is hidden at this width and grid view has its own footer
              count (see #311). */}
          {isMobile && viewMode !== VIEW_MODES.GRID && (
            <span className="project-count--map">
              {targetedProjects.length} project{targetedProjects.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Saved Searches Modal */}
      <SavedSearchesModal
        isOpen={showSavedSearchesModal}
        onClose={() => setShowSavedSearchesModal(false)}
        searches={savedSearches}
        onLoadSearch={handleLoadSearch}
        onDeleteSearch={handleDeleteSearch}
        onUpdateSearch={handleUpdateSearch}
        onToggleNotifications={handleToggleNotifications}
        onExportSearch={handleOpenExportFromSavedSearch}
        onSaveCurrentSearch={handleSaveFromModal}
        currentFilters={activeFilters}
        isSubscriber={userInfo.isSubscriber}
        loading={savedSearchesLoading}
      />

      {/* Export Data Modal (Pro-only) */}
      <ExportDataModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        filters={exportContext.filters}
        projectNids={exportContext.projectNids}
        searchName={exportContext.searchName}
        savedSearchId={exportContext.savedSearchId}
        quota={exportQuota}
        quotaLoading={exportQuotaLoading}
        onExport={handleExport}
      />

      {/* Pro Upgrade Modal (for non-subscribers) */}
      <ProUpgradeModal
        isOpen={showProUpgradeModal}
        onClose={() => setShowProUpgradeModal(false)}
        feature={proUpgradeFeature}
      />

      {/* Overview Video Modal (help button + ?overview=1 deep link) */}
      <OverviewVideoModal
        isOpen={showOverviewModal}
        onClose={() => setShowOverviewModal(false)}
      />
      {/* Notifications now render via the site-wide status ribbon (Drupal.Message). */}
    </div>
  );
}

export default App;
